/**
 * G2eGente - Componente de Verificação de Aprovação de Empresa
 * 
 * Exibe uma tela de acesso limitado quando a empresa não está aprovada pelo admin.
 * Permite visualização mas bloqueia ações de criação/edição.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { companiesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertTriangle, Clock, Eye, Lock, CheckCircle2 } from 'lucide-react';

const CompanyApprovalContext = createContext(null);

export const useCompanyApproval = () => {
  const context = useContext(CompanyApprovalContext);
  if (!context) {
    // Return default values if not within provider
    return { 
      isApproved: true, 
      isLoading: false, 
      checkApproval: () => true,
      company: null
    };
  }
  return context;
};

export const CompanyApprovalProvider = ({ children }) => {
  const { user } = useAuth();
  const [isApproved, setIsApproved] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const checkCompanyApproval = async () => {
      // Only check for COMPANY users
      if (!user || user.role !== 'COMPANY') {
        setIsApproved(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await companiesAPI.getMe();
        const companyData = response.data;
        setCompany(companyData);
        // Companies sem o campo aprovada são consideradas aprovadas (retrocompatibilidade)
        const approved = companyData.aprovada !== false;
        setIsApproved(approved);
      } catch (error) {
        console.error('Error checking company approval:', error);
        // Em caso de erro, assume aprovada para não bloquear
        setIsApproved(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkCompanyApproval();
  }, [user]);

  // Função para verificar aprovação antes de ações
  const checkApproval = (showToast = true) => {
    if (!isApproved && showToast) {
      // Pode ser usado para mostrar toast quando usuário tenta ação bloqueada
      return false;
    }
    return isApproved;
  };

  return (
    <CompanyApprovalContext.Provider value={{ isApproved, isLoading, checkApproval, company }}>
      {children}
    </CompanyApprovalContext.Provider>
  );
};

/**
 * Banner de aviso de aprovação pendente
 */
export const PendingApprovalBanner = () => {
  const { isApproved, isLoading } = useCompanyApproval();

  if (isLoading || isApproved) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="flex items-center gap-3 max-w-7xl mx-auto">
        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Clock className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">
            Sua empresa está aguardando aprovação
          </p>
          <p className="text-xs text-amber-600">
            Você pode visualizar a plataforma, mas algumas funcionalidades estão bloqueadas até a aprovação.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Tela de acesso limitado para empresas não aprovadas
 */
export const RestrictedAccessOverlay = ({ children, action = "criar" }) => {
  const { isApproved, isLoading } = useCompanyApproval();

  if (isLoading) return children;
  if (isApproved) return children;

  return (
    <div className="relative">
      {/* Conteúdo original com opacidade reduzida */}
      <div className="opacity-50 pointer-events-none select-none">
        {children}
      </div>
      
      {/* Overlay de bloqueio */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
        <Card className="max-w-md mx-4 border-amber-200 bg-amber-50/90">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-amber-900">Acesso Restrito</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-amber-700 text-sm mb-4">
              Sua empresa está aguardando aprovação do administrador da plataforma.
            </p>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2 justify-center">
                <Eye className="w-4 h-4" />
                Modo Visualização
              </h4>
              <p className="text-xs text-slate-600">
                Você pode explorar a plataforma e conhecer suas funcionalidades. 
                Após a aprovação, todas as ações serão liberadas.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * HOC para proteger páginas que requerem aprovação
 */
export const withApprovalCheck = (WrappedComponent, options = {}) => {
  const { blockCompletely = false } = options;

  return function ApprovalCheckedComponent(props) {
    const { isApproved, isLoading } = useCompanyApproval();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full"></div>
        </div>
      );
    }

    if (!isApproved && blockCompletely) {
      return (
        <div className="p-8">
          <Card className="max-w-lg mx-auto border-amber-200 bg-amber-50">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <CardTitle className="text-amber-900">Acesso Não Disponível</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-amber-700">
                Esta funcionalidade estará disponível após a aprovação da sua empresa.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <WrappedComponent {...props} isCompanyApproved={isApproved} />;
  };
};

/**
 * Botão que verifica aprovação antes de executar ação
 */
export const ApprovalGuardedButton = ({ 
  children, 
  onClick, 
  disabled,
  className,
  ...props 
}) => {
  const { isApproved } = useCompanyApproval();

  const handleClick = (e) => {
    if (!isApproved) {
      e.preventDefault();
      // Pode mostrar um toast aqui
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || !isApproved}
      className={`${className} ${!isApproved ? 'opacity-50 cursor-not-allowed' : ''}`}
      title={!isApproved ? 'Aguardando aprovação da empresa' : undefined}
      {...props}
    >
      {children}
    </button>
  );
};

export default CompanyApprovalContext;
